import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { addDoc, collection, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { AnonymousComplaintCategory } from '@/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { cn } from '@/lib/utils';
import { Lock, ShieldCheck } from 'lucide-react';

const CATEGORY_OPTIONS: { value: AnonymousComplaintCategory; label: string }[] = [
  { value: 'violencia', label: 'Violencia laboral' },
  { value: 'riesgo_psicosocial', label: 'Riesgo psicosocial' },
  { value: 'entorno_organizacional', label: 'Entorno organizacional' },
  { value: 'otro', label: 'Otro' },
];

const selectClassName = cn(
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  'disabled:cursor-not-allowed disabled:opacity-50'
);

const textareaClassName = cn(
  'flex min-h-[140px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
  'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  'disabled:cursor-not-allowed disabled:opacity-50'
);

export function ComplaintSubmissionPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const [companyReady, setCompanyReady] = useState<'loading' | 'ok' | 'invalid'>('loading');
  const [category, setCategory] = useState<AnonymousComplaintCategory>('riesgo_psicosocial');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!companyId?.trim()) {
      setCompanyReady('invalid');
      return;
    }
    let cancelled = false;
    getDoc(doc(db, 'companies', companyId.trim()))
      .then((snap) => {
        if (cancelled) return;
        setCompanyReady(snap.exists() ? 'ok' : 'invalid');
      })
      .catch(() => {
        if (!cancelled) setCompanyReady('invalid');
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!companyId?.trim() || companyReady !== 'ok') return;
    setError(null);
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'complaints'), {
        companyId: companyId.trim(),
        category,
        subject: subject.trim(),
        description: description.trim(),
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setSuccess(true);
    } catch {
      setError('No se pudo enviar el reporte. Intenta de nuevo más tarde o verifica tu conexión.');
    } finally {
      setSubmitting(false);
    }
  };

  if (companyReady === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
        <p className="text-sm text-muted-foreground">Cargando…</p>
      </div>
    );
  }

  if (companyReady === 'invalid' || !companyId?.trim()) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
        <Card className="max-w-md w-full border-destructive/30">
          <CardHeader>
            <CardTitle>Enlace no válido</CardTitle>
            <CardDescription>
              Este enlace de buzón no existe o ya no está disponible. Si necesitas presentar una queja, solicita el
              enlace actualizado a tu área de Recursos Humanos.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
        <Card className="max-w-lg w-full border-emerald-600/20 shadow-sm">
          <CardHeader className="space-y-2">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-600/10 text-emerald-700">
              <ShieldCheck className="h-7 w-7" aria-hidden />
            </div>
            <CardTitle>Reporte enviado</CardTitle>
            <CardDescription className="text-base text-foreground/90 leading-relaxed">
              Tu mensaje se registró de forma <strong>100% anónima</strong>. No guardamos datos personales, dirección
              IP ni información del dispositivo. El área responsable revisará el contenido según los protocolos
              internos y la normativa aplicable.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Puedes cerrar esta ventana. No es necesario realizar ninguna otra acción.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted/50 to-muted/20 px-4 py-10">
      <div className="mx-auto max-w-xl space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">Buzón de denuncias anónimas</h1>
          <p className="text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
            Espacio confidencial para reportar situaciones relacionadas con seguridad y salud en el trabajo. Tu
            identidad no se solicita ni se registra.
          </p>
        </div>

        <Card className="shadow-sm">
          <CardHeader className="space-y-3 border-b bg-card/80 pb-4">
            <div className="flex items-start gap-3 rounded-lg border border-primary/15 bg-primary/5 p-3 text-sm">
              <Lock className="h-5 w-5 shrink-0 text-primary mt-0.5" aria-hidden />
              <p className="text-muted-foreground leading-relaxed">
                La información que envíes es anónima. Redacta con claridad y evita incluir datos que te identifiquen si
                deseas mantener el máximo anonimato.
              </p>
            </div>
            <CardTitle className="text-lg">Nuevo reporte</CardTitle>
            <CardDescription>Los campos marcados son obligatorios.</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="category">Categoría</Label>
                <select
                  id="category"
                  className={selectClassName}
                  value={category}
                  onChange={(ev) => setCategory(ev.target.value as AnonymousComplaintCategory)}
                  disabled={submitting}
                  required
                >
                  {CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="subject">Asunto</Label>
                <Input
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Resumen breve"
                  minLength={3}
                  maxLength={300}
                  required
                  disabled={submitting}
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground">Entre 3 y 300 caracteres.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Descripción</Label>
                <textarea
                  id="description"
                  className={textareaClassName}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe los hechos con el mayor detalle posible."
                  minLength={10}
                  maxLength={8000}
                  required
                  disabled={submitting}
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground">Mínimo 10 caracteres.</p>
              </div>

              {error && (
                <div
                  role="alert"
                  className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  {error}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? 'Enviando…' : 'Enviar de forma anónima'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
